import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { AUTOPARK_TRANSPORT_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	getAutoparkTransportInfoRequest,
	createAutoparkTransportRequest,
	updateAutoparkTransportRequest,
	deleteAutoparkTransportRequest,
	getAutoParkTransportTariffTypesRequest,
	getAddressesRequest,
	getVeerouteTransportTypesRequest,
} from '../../api/requests';
import {
	GET_AUTOPARK_TRANSPORT_INFO,
	setAutoparkTransportInfoAction,
	setIsAutoparkTransportInfoLoadingAction,
	GET_TRANSPORT_TYPES_LIST,
	setTransportTypesListAction,
	setIsTransportTypesListLoadingAction,
	GET_AUTOPARK_TRANSPORT_TARIFF_TYPES_LIST,
	setAutoparkTransportTariffTypesListAction,
	setIsAutoparkTransportTariffTypesListLoadingAction,
	GET_ADDRESSES_LIST,
	setAddressesListAction,
	setIsAddressesListLoadingAction,
	CREATE_AUTOPARK_TRANSPORT,
	setIsAutoparkTransportCreatingAction,
	UPDATE_AUTOPARK_TRANSPORT,
	setIsAutoparkTransportUpdatingAction,
	DELETE_AUTOPARK_TRANSPORT,
	setIsAutoparkTransportDeletingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { transformToValidationErrors } from './utils';

export function* getAutoparkTransportInfoSaga({ payload: { autoparkId, transportId } }) {
	try {
		yield put(setIsAutoparkTransportInfoLoadingAction(true));

		const { data, message, toast } = yield call(getAutoparkTransportInfoRequest, autoparkId, transportId);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setAutoparkTransportInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkTransportInfoLoadingAction(false));
	}
}

export function* getTransportTypesListSaga() {
	try {
		yield put(setIsTransportTypesListLoadingAction(true));

		const { data, message, toast } = yield call(
			getVeerouteTransportTypesRequest,
			'?order.column=id&order.direct=asc&receive=all',
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTransportTypesListAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTransportTypesListLoadingAction(false));
	}
}

export function* getAutoparkTransportTariffTypesListSaga() {
	try {
		yield put(setIsAutoparkTransportTariffTypesListLoadingAction(true));

		const { data, message } = yield call(
			getAutoParkTransportTariffTypesRequest,
			'?order.column=id&order.direct=asc&receive=all',
		);

		if (data) {
			yield put(setAutoparkTransportTariffTypesListAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkTransportTariffTypesListLoadingAction(false));
	}
}

export function* getAddressesSaga() {
	try {
		yield put(setIsAddressesListLoadingAction(true));

		const { data, message } = yield call(getAddressesRequest, '?receive=all');

		if (data) {
			yield put(setAddressesListAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAddressesListLoadingAction(false));
	}
}

export function* createAutoparkTransportSaga({ payload: { redirect, autoparkId, searchQuery } }) {
	try {
		yield put(setIsAutoparkTransportCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createAutoparkTransportRequest, autoparkId, dataForSave);

		if (data) {
			const str = removeModalQuery(searchQuery);

			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${str ? `&${str}` : ''}` } : {}),
					}),
				);
			}

			redirect();
		} else {
			if (errors) {
				yield put(touch(AUTOPARK_TRANSPORT_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(AUTOPARK_TRANSPORT_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkTransportCreatingAction(false));
	}
}

export function* updateAutoparkTransportSaga({ payload: { autoparkId, transportId, redirect, searchQuery } }) {
	try {
		yield put(setIsAutoparkTransportUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(
			updateAutoparkTransportRequest,
			autoparkId,
			transportId,
			dataForSave,
		);

		if (data) {
			const str = removeModalQuery(searchQuery);

			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${str ? `&${str}` : ''}` } : {}),
					}),
				);
			}

			yield put(setAutoparkTransportInfoAction(data));

			redirect();
		} else {
			if (errors) {
				yield put(touch(AUTOPARK_TRANSPORT_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(AUTOPARK_TRANSPORT_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkTransportUpdatingAction(false));
	}
}

export function* deleteAutoparkTransportSaga({ payload: { autoparkId, transportId, redirect } }) {
	try {
		yield put(setIsAutoparkTransportDeletingAction(true));

		const { data, message, toast } = yield call(deleteAutoparkTransportRequest, autoparkId, transportId);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkTransportDeletingAction(false));
	}
}

export default function* autoparkModalSaga() {
	yield takeEvery(GET_AUTOPARK_TRANSPORT_INFO, getAutoparkTransportInfoSaga);
	yield takeEvery(GET_TRANSPORT_TYPES_LIST, getTransportTypesListSaga);
	yield takeEvery(GET_AUTOPARK_TRANSPORT_TARIFF_TYPES_LIST, getAutoparkTransportTariffTypesListSaga);
	yield takeEvery(GET_ADDRESSES_LIST, getAddressesSaga);
	yield takeEvery(CREATE_AUTOPARK_TRANSPORT, createAutoparkTransportSaga);
	yield takeEvery(UPDATE_AUTOPARK_TRANSPORT, updateAutoparkTransportSaga);
	yield takeEvery(DELETE_AUTOPARK_TRANSPORT, deleteAutoparkTransportSaga);
}
